// =============================================================================
// Legal pages — Privacy Policy, Terms of Use, Accessibility Statement (Hebrew)
// =============================================================================
// BASELINE drafts to satisfy the Israeli minimum for a public site that collects
// personal + politically-sensitive data (PPL Amendment 13, 2025) and must be
// accessible (Equal Rights for Persons with Disabilities regs / WCAG 2.0 AA).
// A licensed Israeli privacy/tech lawyer should review the privacy policy given
// the special-category (political affiliation) data. Contact + legal-entity name
// are placeholders to confirm.
// =============================================================================

import type { ReactNode } from "react"
import { Link } from "react-router-dom"
import { PageShell } from "@/components/brand/PageShell"

const LAST_UPDATED = "12 ביולי 2026"
const CONTACT_EMAIL = "privacy@elhadegel-friends.com"

function LegalShell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <PageShell bg="subtle">
      <main dir="rtl" className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
        <h1 className="text-3xl lg:text-4xl font-extrabold text-[#0F1B36] mb-2 leading-tight">
          {title}
        </h1>
        <p className="text-xs text-[#6B7280] mb-8">עודכן לאחרונה: {LAST_UPDATED}</p>
        <div className="space-y-5 text-[15px] leading-relaxed text-[#374151] [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-[#1B2D52] [&_h2]:mt-8 [&_h2]:mb-2 [&_ul]:list-disc [&_ul]:pr-5 [&_ul]:space-y-1 [&_a]:text-[#1B2D52] [&_a]:underline">
          {children}
        </div>
        <LegalNav />
      </main>
    </PageShell>
  )
}

export function LegalNav({ className = "" }: { className?: string }) {
  return (
    <nav
      dir="rtl"
      className={`mt-10 pt-6 border-t border-[#E5E7EB] flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-[#6B7280] ${className}`}
    >
      <Link to="/privacy" className="hover:text-[#1B2D52]">מדיניות פרטיות</Link>
      <span className="opacity-40">·</span>
      <Link to="/terms" className="hover:text-[#1B2D52]">תנאי שימוש</Link>
      <span className="opacity-40">·</span>
      <Link to="/accessibility" className="hover:text-[#1B2D52]">הצהרת נגישות</Link>
      <span className="opacity-40">·</span>
      <Link to="/" className="hover:text-[#1B2D52]">חזרה לדף הבית</Link>
    </nav>
  )
}

// -- Privacy Policy ----------------------------------------------------------
export function PrivacyPage() {
  return (
    <LegalShell title="מדיניות פרטיות">
      <p>
        תנועת <strong>אל הדגל</strong> ("התנועה", "אנחנו") מכבדת את פרטיותך. מדיניות זו מסבירה
        איזה מידע אנו אוספים באתר <strong>elhadegel-friends.com</strong> ("האתר"), כיצד אנו
        משתמשים בו, וכיצד אנו שומרים עליו — בהתאם לחוק הגנת הפרטיות, התשמ״א־1981 ותיקוניו
        (לרבות תיקון 13).
      </p>

      <h2>איזה מידע אנו אוספים</h2>
      <ul>
        <li>פרטי הרשמה שאתה מוסר: שם פרטי, שם משפחה, מספר טלפון, כתובת דוא״ל ויישוב מגורים.</li>
        <li>עצם הצטרפותך לתנועה, המשקפת תמיכה פוליטית — מידע זה מסווג כ״מידע בעל רגישות גבוהה״.</li>
        <li>מידע טכני בסיסי הנאסף אוטומטית לצורך אבטחה ותפעול: סוג דפדפן ומכשיר, ונתוני שימוש
          בסיסיים (כגון לחיצה על קישור הזמנה).</li>
      </ul>

      <h2>לשם מה אנו משתמשים במידע</h2>
      <ul>
        <li>ניהול רשימת התומכים ובניית מפת התמיכה הארצית של התנועה.</li>
        <li>ייחוס הצטרפות למי שהזמין אותך (מנגנון ההזמנה האישי).</li>
        <li>יצירת קשר ועדכונים הנוגעים לפעילות התנועה.</li>
        <li>דיוור שיווקי — <strong>רק אם נתת לכך הסכמה מפורשת</strong> בעת ההרשמה, וניתן להסיר
          אותה בכל עת.</li>
      </ul>

      <h2>בסיס ההסכמה</h2>
      <p>
        מסירת המידע והשימוש בו נעשים על סמך הסכמתך. לגבי המידע בדבר עמדתך הפוליטית — שהוא מידע
        רגיש — הצטרפותך מהווה <strong>הסכמה מפורשת</strong> לאיסופו ולשימוש בו למטרות המפורטות
        לעיל. אינך חייב למסור מידע, אך ללא פרטים בסיסיים לא נוכל לרשום אותך.
      </p>

      <h2>אבטחת מידע</h2>
      <p>
        אנו נוקטים אמצעי אבטחה מקובלים ומוגברים בשל רגישות המידע: כתובת הדוא״ל והטלפון נשמרים
        <strong> מוצפנים </strong>במסד הנתונים, הגישה מוגבלת לגורמים מורשים בלבד, והתקשורת מאובטחת
        (HTTPS).
      </p>

      <h2>העברת מידע לצדדים שלישיים</h2>
      <p>
        איננו מוכרים ואיננו מעבירים את פרטיך למפרסמים. אנו נעזרים בספקי תשתית וענן המעבדים מידע
        עבורנו בלבד ובכפוף להתחייבויות אבטחה ופרטיות. מידע עשוי להימסר אם נחויב לכך על פי דין.
      </p>

      <h2>עוגיות (Cookies)</h2>
      <p>
        האתר עושה שימוש בעוגיות הכרחיות לצורך ההתחברות והאבטחה. אלו חיוניות לתפקוד השירות.
      </p>

      <h2>שמירת המידע</h2>
      <p>
        אנו שומרים את המידע כל עוד התנועה פעילה ולצורך המטרות שלעיל, או עד לבקשתך למחיקה, בכפוף
        לחובות שמירה על פי דין.
      </p>

      <h2>זכויותיך</h2>
      <p>
        זכותך לעיין במידע שנאסף עליך, לבקש את תיקונו או מחיקתו, ולחזור בך מהסכמתך לדיוור. לפניות
        בנושאי פרטיות: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>

      <h2>שינויים במדיניות</h2>
      <p>
        אנו רשאים לעדכן מדיניות זו מעת לעת. הגרסה המעודכנת תפורסם בעמוד זה עם תאריך העדכון.
      </p>
    </LegalShell>
  )
}

// -- Terms of Use ------------------------------------------------------------
export function TermsPage() {
  return (
    <LegalShell title="תנאי שימוש">
      <p>
        השימוש באתר <strong>elhadegel-friends.com</strong> של תנועת <strong>אל הדגל</strong> כפוף
        לתנאים שלהלן. עצם השימוש באתר וההרשמה בו מהווים הסכמה לתנאים אלה. התנאים מנוסחים בלשון זכר
        מטעמי נוחות בלבד ומופנים לכל המגדרים.
      </p>

      <h2>השירות</h2>
      <p>
        האתר מאפשר להצטרף לתנועה, לצפות בסרטון היכרות, להירשם, ולקבל קישור הזמנה אישי לשיתוף.
        מטרת הפרויקט היא לאגד תומכים לקראת היעד של 150,000.
      </p>

      <h2>התחייבויות המשתמש</h2>
      <ul>
        <li>למסור מידע נכון, מדויק ועדכני, ולהירשם עבור עצמך בלבד.</li>
        <li>לא לעשות שימוש לרעה באתר, לא לפגוע בפעילותו התקינה ולא לנסות לגשת אליו שלא כדין.</li>
        <li>לא להתחזות לאדם או גוף אחר ולא למסור פרטים כוזבים.</li>
      </ul>

      <h2>קניין רוחני</h2>
      <p>
        כל התכנים באתר — לרבות הטקסטים, העיצוב, הלוגו והסרטונים — הם קניינה של התנועה או של מי
        מטעמה, ואין לעשות בהם שימוש ללא רשות.
      </p>

      <h2>הגבלת אחריות</h2>
      <p>
        האתר מוצע כפי שהוא ("As Is"). אנו עושים מאמץ לשמור על זמינות ותקינות, אך איננו מתחייבים
        לפעילות רציפה או נטולת תקלות, ולא נישא באחריות לנזק עקיף הנובע מהשימוש באתר, ככל שהדבר מותר
        על פי דין.
      </p>

      <h2>דין וסמכות שיפוט</h2>
      <p>
        על תנאים אלה יחולו דיני מדינת ישראל, וסמכות השיפוט הבלעדית נתונה לבתי המשפט המוסמכים בישראל.
      </p>

      <h2>שינויים בתנאים</h2>
      <p>אנו רשאים לעדכן תנאים אלה מעת לעת; הנוסח המחייב הוא זה המפורסם בעמוד זה.</p>

      <h2>יצירת קשר</h2>
      <p>לשאלות בנוגע לתנאי השימוש: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</p>
    </LegalShell>
  )
}

// -- Accessibility Statement -------------------------------------------------
export function AccessibilityPage() {
  return (
    <LegalShell title="הצהרת נגישות">
      <p>
        תנועת <strong>אל הדגל</strong> רואה חשיבות רבה במתן שירות שוויוני לכלל הגולשים, ופועלת
        להנגשת האתר בהתאם לתקנות שוויון זכויות לאנשים עם מוגבלות (התאמות נגישות לשירות), התשע״ג־2013,
        ולתקן הישראלי ת״י 5568 המבוסס על הנחיות <strong>WCAG 2.0 ברמת AA</strong>.
      </p>

      <h2>מה נעשה באתר</h2>
      <ul>
        <li>מבנה סמנטי ותמיכה בניווט באמצעות מקלדת.</li>
        <li>ניגודיות צבעים וגודל טקסט קריאים, ותאימות לקוראי מסך.</li>
        <li>תמיכה בהתאמת גודל התצוגה בדפדפן.</li>
      </ul>

      <h2>מגבלות ידועות</h2>
      <p>
        ייתכן שחלקים מסוימים באתר טרם הונגשו במלואם. אנו ממשיכים לשפר את הנגישות באופן שוטף. אם
        נתקלת בקושי, נשמח שתדווח לנו ונפעל לתקן.
      </p>

      <h2>פנייה בנושא נגישות</h2>
      <p>
        לתגובות ולפניות בנושא נגישות ניתן לפנות לרכז/ת הנגישות של התנועה בכתובת:{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. נשתדל להשיב בהקדם.
      </p>

      <p className="text-xs text-[#6B7280]">הצהרה זו עודכנה בתאריך {LAST_UPDATED}.</p>
    </LegalShell>
  )
}
